<script lang="ts">
	//Formating
	import { format } from '../../services/i18n';
	//Design system components
	import { BarChart, ControlsContainer, Loader, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar } from '../../helpers/utilities';
	//Chart Config
	import { getDynamicChartConfig } from 'src/helpers/statistics/config/dynamic_statistics';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { ChartDataInterface } from 'src/types/Chart';
	import LayerChartSpacing from 'src/components/Chart/LayerChart/LayerChartSpacing.svelte';

	export let roomsCurrentConsList = {};

	type SeriesName = 'current' | 'excluded';

	const seriesColors: Record<SeriesName, string> = {
		current: '#25b684',
		excluded: '#363e3b',
	};
	let seriesVisibility: Record<SeriesName, boolean> = {
		current: true,
		excluded: true,
	};
	let chartData: ChartDataInterface;
	let chartContainer: HTMLDivElement;
	let tooltipRoom: string | null = null;
	let tooltipX = 0;
	let tooltipY = 0;
	let roomLabelLookup: Record<string, string> = {};

	const MAX_ROOM_LABEL_LENGTH = 10;
	const getDisplayRoomLabel = (label: string) =>
		label.length > MAX_ROOM_LABEL_LENGTH
			? `${label.slice(0, MAX_ROOM_LABEL_LENGTH - 1).trimEnd()}…`
			: label;
	const getFullRoomLabel = (label: string) => roomLabelLookup[label] || label;

	const includedRooms = {},
		generateChartData = (roomsObjList) => {
			let currentValues = [],
				excludedValues = [],
				labels = [];
			const nextRoomLabelLookup: Record<string, string> = {};
			Object.values(roomsObjList).forEach((el: any) => {
				const isConsumption = el.in_power || el.ex_power;
				if (isConsumption || includedRooms[el.id]) {
					if (!includedRooms[el.id]) {
						includedRooms[el.id] = 1;
					}
					currentValues = [...currentValues, el.in_power];
					excludedValues = [...excludedValues, el.ex_power];
					const fullLabel = String(el.title || '');
					const displayLabel = getDisplayRoomLabel(fullLabel);
					labels = [...labels, displayLabel];
					nextRoomLabelLookup[displayLabel] = fullLabel;
				}
			});
			roomLabelLookup = nextRoomLabelLookup;
			return {
				currentValues,
				excludedValues,
				labels,
				xGrid: true,
			};
		};

	const toggleSeries = (seriesName: SeriesName) => {
		seriesVisibility = {
			...seriesVisibility,
			[seriesName]: !seriesVisibility[seriesName],
		};
	};
	const getSeriesColor = (seriesName: string) => seriesColors[seriesName as SeriesName];
	const onToggleSeries = (seriesName: string) => toggleSeries(seriesName as SeriesName);
	const getTooltipRows = () =>
		[...(chartData.labels || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
	const getTooltipValue = (seriesName: string, roomName: string) => {
		const series = chartData.series.find((item) => item.name === seriesName);
		return Number(series?.data?.find((point) => String(point.dt) === roomName)?.value ?? 0);
	};
	const onChartPointerMove = (event: PointerEvent) => {
		const target = event.target as Element;
		if (!target?.classList?.contains('lc-tooltip-overlay')) return;

		const rows = getTooltipRows();
		if (!rows.length) return;

		const plotBounds = target.getBoundingClientRect();
		const chartBounds = chartContainer.getBoundingClientRect();
		const relativeY = Math.max(0, Math.min(event.clientY - plotBounds.top, plotBounds.height - 1));
		const rowIndex = Math.min(
			rows.length - 1,
			Math.floor((relativeY / plotBounds.height) * rows.length),
		);

		tooltipRoom = rows[rowIndex];
		tooltipX = Math.min(event.clientX - chartBounds.left + 12, chartBounds.width - 150);
		tooltipY = Math.max(8, event.clientY - chartBounds.top - 24);
	};

	$: {
		const nextChartData = getDynamicChartConfig(generateChartData(roomsCurrentConsList));
		chartData = {
			...nextChartData,
			series: nextChartData.series.map((series) => ({
				...series,
				visible: seriesVisibility[series.name as SeriesName],
			})),
		};
	}
</script>

<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
	<svelte:fragment slot="first">
		<Text class="text-bold"
			>{capFirstChar(
				$format('text_energy_distribution_by_rooms', {
					default: 'Energy Distribution By Rooms',
				}),
			)}</Text
		>
		{#if !chartData.loaded}
			<div
				class="d-flex justify-content-center align-items-center"
				style={`height: ${chartData.height}px`}
			>
				<Loader size="m" />
			</div>
		{:else}
			<div
				class="room-chart-container"
				role="presentation"
				bind:this={chartContainer}
				on:pointermove={onChartPointerMove}
				on:pointerleave={() => (tooltipRoom = null)}
			>
				<LayerChartSpacing>
					<BarChart {chartData} orientation="horizontal" legend={false} />
				</LayerChartSpacing>
				{#if tooltipRoom}
					<div
						class="room-chart-tooltip"
						style:left={`${tooltipX}px`}
						style:top={`${tooltipY}px`}
					>
						<strong>{getFullRoomLabel(tooltipRoom)}</strong>
						{#each chartData.series.filter((series) => series.visible) as series}
							<div class="room-chart-tooltip-row">
								<span
									class="room-chart-tooltip-dot"
									style={`--dot-color: ${getSeriesColor(series.name)}`}
								></span>
								<span>{capFirstChar(series.name)}</span>
								<strong>
									{Math.round(getTooltipValue(series.name, tooltipRoom) * 10) / 10}
									W
								</strong>
							</div>
						{/each}
					</div>
				{/if}
			</div>
			<div
				class="chart-legend"
				role="group"
				aria-label={$format('label_chart_series', { default: 'Chart series' })}
			>
				{#each chartData.series as series}
					<button
						type="button"
						class:hidden={!series.visible}
						aria-pressed={series.visible}
						style={`--dot-color: ${getSeriesColor(series.name)}`}
						on:click={() => onToggleSeries(series.name)}
					>
						{capFirstChar(series.name)}
					</button>
				{/each}
			</div>
		{/if}
	</svelte:fragment>
</ControlsContainer>

<style>
	.room-chart-container {
		position: relative;
		overflow: visible;
	}

	.room-chart-tooltip {
		position: absolute;
		z-index: 2;
		display: grid;
		gap: 6px;
		min-width: 138px;
		padding: 8px 10px;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 8px;
		background: rgba(22, 24, 27, 0.96);
		color: white;
		font-size: 11px;
		pointer-events: none;
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
	}

	.room-chart-tooltip-row {
		display: grid;
		grid-template-columns: auto 1fr auto;
		align-items: center;
		gap: 6px;
	}

	.room-chart-tooltip-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--dot-color);
	}

	.chart-legend {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 6px;
		margin: 10px 20px 16px;
		padding: 0 10px;
	}

	.chart-legend button {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 0;
		border: 0;
		background: transparent;
		color: hsl(220 15% 85%);
		font: inherit;
		font-size: 10px;
		cursor: pointer;
	}

	.chart-legend button::before {
		content: '';
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--dot-color);
		outline: 1px solid rgba(0, 0, 0, 0.25);
		outline-offset: 1px;
	}

	.chart-legend button.hidden {
		text-decoration: line-through;
	}
</style>
